/**
 * M3 travel tuning (plan R6, §2.2): `src/data/reach-travel.json`, Zod-validated here. Cruise law, mass-lock radii,
 * interdiction tug-of-war, trade lanes and relay transit (D-TBD 2, 15, 17, 19). Every value is a starting value,
 * tuned in playtest; R17 exposes them in the dev panel.
 */
import { z } from 'zod'
import travelJson from '../data/reach-travel.json'
import { cruiseChargeSOf, cruiseLagSOf, loadBandsConfig, type BandsConfig } from '../data/bands'
import type { ShipBand } from '../data/schemas'

const positive = z.number().positive()
const nonNeg = z.number().nonnegative()
const fraction = z.number().min(0).max(1)
const angleDeg = z.number().positive().max(180)
/** A [min, max] range with min ≤ max. */
const range = (item: z.ZodNumber): z.ZodType<readonly [number, number]> =>
  z.tuple([item, item]).refine(([lo, hi]) => lo <= hi, {
    message: 'range min must not exceed max',
  })

export const PILOT_PROFILES = ['rookie', 'veteran', 'ace'] as const
export type PilotProfile = (typeof PILOT_PROFILES)[number]
const profile = z.enum(PILOT_PROFILES)

const perRegion = <T extends z.ZodTypeAny>(item: T): z.ZodObject<{ gate: T; mid: T; deep: T; rim: T }> =>
  z.object({ gate: item, mid: item, deep: item, rim: item })

const cruiseSchema = z
  .object({
    comment: z.string().optional(),
    /** Hold time (s) to engage the drive. */
    chargeS: positive,
    /** The cruise law's gain (s⁻¹): allowed = clamp(k × dEff, vMin, vMax). */
    k: positive,
    vMin: positive,
    vMax: positive,
    /** First-order lag τ (s) of the ship's speed toward throttle × allowed; 0 = instant. */
    lagS: nonNeg,
    /** dEff never exceeds this × the distance to the nearest shell. */
    surfaceFactor: positive,
    /** dEff with no nav target. */
    freeDistanceM: positive,
    /** Pitch/yaw/roll rate scale while detached (cruise, lanes). */
    rateScale: fraction,
    /** The auto-drop distance to the target (to a body's shell or a field's sphere; to the centre of the rest). */
    dropDistanceM: positive,
    /**
     * The auto-drop distance off a station's hull (scale build SC-5c): a station arrival drops at R + this from its
     * centre. Absent: stations drop `dropDistanceM` from the centre, as before.
     */
    stationDropFromHullM: positive.optional(),
    dropSpeed: nonNeg,
    manualCooldownS: nonNeg,
    emergencyCooldownS: nonNeg,
    /** Throttle change per second at full axis. */
    throttleRate: positive,
    minThrottle: fraction,
    /** Within this of the target the velocity points exactly at it. */
    alignAssistDeg: angleDeg,
    /** Below this speed streaming resumes after a cruise. */
    streamResumeSpeed: positive,
  })
  .refine((c) => c.vMin <= c.vMax, {
    message: 'cruise.vMin must not exceed vMax',
  })

const massLockSchema = z.object({
  comment: z.string().optional(),
  /** Mass-lock range off a station's hull (spec §4.2; scale build SC-5a2a): R + stationM from its centre. */
  stationM: positive,
  relayM: positive,
  /** Mass-lock radius around a large ship. */
  shipM: positive,
  /** Whether a hostile weapon lock blocks the drive. */
  weaponLock: z.boolean(),
  /** The star's shell (mass-lock and emergency drop) = radius × factor + extra, like a body's (D-TBD 19). */
  starShellFactor: z.number().min(1),
  starShellExtraM: nonNeg,
})

const spawnShip = z.object({
  def: z.string().min(1),
  profile,
  count: z.number().int().positive(),
})

const interdictionSchema = z.object({
  comment: z.string().optional(),
  ratePerMin: perRegion(nonNeg),
  rollEveryS: positive,
  graceAfterArrivalS: nonNeg,
  cooldownS: nonNeg,
  stationExclusionM: nonNeg,
  /** Seconds (total) with the nose inside the cone to escape. */
  escapeS: positive,
  /** Seconds for the pull meter to fill while on the vector. */
  maxS: positive,
  coneDeg: angleDeg,
  /** Escape lost per second outside the cone, as a fraction of dt. */
  escapeDecay: nonNeg,
  /** Pull speed-up while off the vector. */
  offVectorPullMult: z.number().min(1),
  startOffsetDeg: range(z.number().min(0).max(180)),
  driftDegPerS: z.object({ rookie: nonNeg, veteran: nonNeg, ace: nonNeg }),
  driftTurnS: range(positive),
  /** While interdicted the speed decays toward this × allowed. */
  speedFloor: fraction,
  dropSpeed: nonNeg,
  failCooldownS: nonNeg,
  spawnRadiusM: range(positive),
  groups: perRegion(z.array(spawnShip).min(1)),
})

const lanesSchema = z.object({
  kMult: positive,
  vMaxMult: positive,
  entryConeDeg: angleDeg,
  entryMaxSpeed: positive,
  exitSpeed: nonNeg,
  exitOffsetM: nonNeg,
})

const transitSchema = z
  .object({
    alignDeg: angleDeg,
    maxSpeed: positive,
    enterS: nonNeg,
    tunnelMinS: nonNeg,
    tunnelMaxS: positive,
    exitS: nonNeg,
    arrivalOffsetM: nonNeg,
    arrivalSpeed: nonNeg,
    hintRangeM: positive,
  })
  .refine((t) => t.tunnelMinS <= t.tunnelMaxS, {
    message: 'transit.tunnelMinS must not exceed tunnelMaxS',
  })

export const travelConfigSchema = z.object({
  comment: z.string().optional(),
  cruise: cruiseSchema,
  massLock: massLockSchema,
  interdiction: interdictionSchema,
  lanes: lanesSchema,
  transit: transitSchema,
})

export type TravelConfig = z.infer<typeof travelConfigSchema>

/** Parses and validates reach-travel.json (throws a ZodError naming the bad field). */
export function loadTravelConfig(raw: unknown = travelJson): TravelConfig {
  return travelConfigSchema.parse(raw)
}

/** bands.json, read once for the per-band cruise timing (M10 P5a7a). */
let bandsCached: BandsConfig | undefined
function bandsConfig(): BandsConfig {
  bandsCached ??= loadBandsConfig()
  return bandsCached
}

/**
 * The cruise spin-up lag τ (s) for the flown hull's band (M10 P5a7a): `bands.json bands.<band>.cruiseLagS`, else the
 * global `cruise.lagS` (also with no band). Allocation-free (per tick).
 */
export function cruiseLagSFor(cfg: TravelConfig, band: ShipBand | null, bands: BandsConfig = bandsConfig()): number {
  if (band === null) return cfg.cruise.lagS
  return cruiseLagSOf(band, bands) ?? cfg.cruise.lagS
}

/**
 * The drive charge hold (s) for the flown hull's band (M10 P5a7a): `bands.json bands.<band>.cruiseChargeS`, else the
 * global `cruise.chargeS` (also with no band). Allocation-free (per tick).
 */
export function cruiseChargeSFor(cfg: TravelConfig, band: ShipBand | null, bands: BandsConfig = bandsConfig()): number {
  if (band === null) return cfg.cruise.chargeS
  return cruiseChargeSOf(band, bands) ?? cfg.cruise.chargeS
}
